import Breadcrumb, {Crumb} from "../../../components/common/Breadcrumbs";
import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {skipToken} from "@reduxjs/toolkit/query";
import {useGetAllStatesQuery} from "../states/statesApi";
import {
    useCreateMetroAreaMutation,
    useDeleteMetroAreaMutation,
    useGetMetroAreaByIdQuery,
    useUpdateMetroAreaMutation
} from "./metroAreasApi";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import {OptionType} from "../../../helpers/utils/StyleUtils";
import {createOptionListForSelectTag} from "../../../helpers/utils/Utils";
import SelectField from "../../../components/common/form/SelectField";
import {useGetAllCountyByStateAbbrQuery} from "../counties/countiesApi";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import {Routes as AppRoutes} from "../../../routes";
import ButtonTray from "../../../components/common/form/ButtonTray";
import ModalButton from "../../../components/common/form/ModalButton";
import IconButton from "../../../components/common/form/IconButton";
import {FaTrash} from "react-icons/fa6";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import {hasPermission} from "../../../helpers/utils/AccessControlUtils";

interface props {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: boolean
}

const MetroAreaForm: FC<props> = ({crumbs, title, btnText, isReadOnly}) => {

    const navigate = useNavigate();
    let {metroAreaId} = useParams();

    const [createMetroArea, createMetroAreaApiResponse] = useCreateMetroAreaMutation();
    const [updateMetroArea, updateMetroAreaApiResponse] = useUpdateMetroAreaMutation();
    const metroAreaByIdApi = useGetMetroAreaByIdQuery(metroAreaId ?? skipToken);
    const [metroAreaData, setMetroAreaData] = useState<any>({});
    const statesApiResult = useGetAllStatesQuery("");
    const countiesApiResult = useGetAllCountyByStateAbbrQuery(metroAreaData?.state?.value ?? skipToken);
    const [deleteApi, {isLoading}] = useDeleteMetroAreaMutation();


    useEffect(() => {
        let obj = metroAreaByIdApi?.data;
        if (obj) {
            setMetroAreaData({
                ...obj,
                state: {
                    label: obj.state?.stateName,
                    value: obj.state?.stateAbbreviation,
                },
                counties: obj.counties?.map((el: any) => ({
                    label: el?.countyName,
                    value: el?.id,
                }))
            })
        }
    }, [metroAreaByIdApi?.data]);

    const handleToggle = (property: string) => (e: any) => {
        setMetroAreaData((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
    };


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let commonObj = {
            ...metroAreaData,
            state: statesApiResult?.data.filter((el: any) => el.stateAbbreviation === metroAreaData.state.value)?.[0],
            counties: metroAreaData.counties?.map((el: any) => {
                return countiesApiResult?.data?.filter((ml: any) => ml.id === el.value)?.[0]
            })
        };

        const createOrUpdateMetroArea = async () => {
            if (btnText === "Add") {
                return await createMetroArea(commonObj).unwrap();
            } else {
                return await updateMetroArea({
                    id: metroAreaId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateMetroArea, (response: any) => {
            if (response?.success || response?.data?.success) {
                setMetroAreaData({});
                navigate("/metro-areas");
            }
        });
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const {name, value} = "target" in e ? e.target : e;

        if (name == "state" && !value) {
            setMetroAreaData({
                ...metroAreaData,
                [name]: value,
                counties: []
            })
        } else {
            setMetroAreaData({
                ...metroAreaData,
                [name]: value,
            })
        }

    }

    const handleDelete = async () => {
        if (metroAreaId) {
            await deleteApi(metroAreaId).unwrap();
        }
        navigate(AppRoutes.MetroAreas.path)
    };

    return (
        <>
            <div className="px-4 py-2">
                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs}/>
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {metroAreaId &&
                            hasPermission(["METR-D"]) &&
                            <ModalButton
                                triggerButton={
                                    <IconButton
                                        icon={<FaTrash/>}
                                        title={"Delete Metro Area"}
                                        hasBackground={true}
                                        className="mr-2"
                                    />
                                }
                                modal={
                                    <ConfirmationModal
                                        show={true}
                                        setShow={() => {
                                        }}
                                        title={"Delete Metro Area"}
                                        bodyMessage={"Are you sure you want to delete this Metro Area?"}
                                        onSubmit={() => handleDelete()}
                                        isLoading={isLoading}
                                    />
                                }
                            />
                        }
                        <AuditableInfoButton obj={metroAreaByIdApi?.data}/>
                    </div>
                </div>

                <div className="flex justify-center mt-3">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">
                                <InputField
                                    name={"name"}
                                    label="Name"
                                    onChange={handleOnChange}
                                    value={metroAreaData?.name || ""}
                                    type="text"
                                    placeholder="Enter Name..."
                                    required={true}
                                    disabled={isReadOnly}
                                />

                                <SelectField
                                    placeholder='Select State...'
                                    value={metroAreaData?.state}
                                    options={createOptionListForSelectTag(statesApiResult?.data, "stateName", "stateAbbreviation")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "state", value: selectedOption})
                                    }}
                                    label={"State"}
                                    required={true}
                                />
                                <SelectField
                                    placeholder='Select Counties...'
                                    value={metroAreaData?.counties}
                                    options={createOptionListForSelectTag(countiesApiResult?.data, "countyName", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "counties", value: selectedOption})
                                    }}
                                    label={"Counties"}
                                    required={true}
                                    isMulti={true}
                                    isDisabled={isReadOnly}
                                />
                                <InputField
                                    label={"Require Fees"}
                                    type={"checkbox"}
                                    checked={metroAreaData?.canRequireFees || ""}
                                    name={"canRequireFees"}
                                    onChange={handleToggle("canRequireFees")}
                                    disabled={isReadOnly}
                                />
                            </div>
                            {!isReadOnly &&
                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            onClick: () => navigate(AppRoutes.MetroAreas.path),
                                                            type: "close",
                                                        },
                                                        buttonType: "button"
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: (createMetroAreaApiResponse.isLoading || updateMetroAreaApiResponse.isLoading),

                                                        },
                                                        buttonType: "button",

                                                    }
                                                ]}
                                                align="center"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>}
                        </>}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}
export default MetroAreaForm;